import axios from "axios";
import { toast } from "sonner";

// Resolve backend URL. Priority:
// 1. REACT_APP_BACKEND_URL (CRA env at build time)
// 2. window.__BACKEND_URL__ (runtime override via /config.js — custom domain)
function resolveBackendURL() {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (envUrl) return envUrl.replace(/\/$/, "");
  if (typeof window !== "undefined" && window.__BACKEND_URL__) {
    return window.__BACKEND_URL__.replace(/\/$/, "");
  }
  return "";
}

const BACKEND_URL = resolveBackendURL();
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Supprime le toast d'erreur des call-sites juste après un 202 pending-approval
// (le message informatif ambre est déjà affiché par l'intercepteur).
const _origToastError = toast.error.bind(toast);
toast.error = (...args) => {
  if (window.__fcSuppressErrorToastUntil && Date.now() < window.__fcSuppressErrorToastUntil) return;
  return _origToastError(...args);
};

// Allow Bearer fallback if cookies not available
let bearerToken = null;
export function setBearerToken(t) {
  bearerToken = t;
  if (t) localStorage.setItem("fc_token", t);
  else localStorage.removeItem("fc_token");
}
export function getBearerToken() {
  if (bearerToken) return bearerToken;
  bearerToken = localStorage.getItem("fc_token");
  return bearerToken;
}

api.interceptors.request.use((config) => {
  const t = getBearerToken();
  if (t) config.headers = { ...config.headers, Authorization: `Bearer ${t}` };
  return config;
});

// Normalize FastAPI error payloads: on 422, `detail` is an ARRAY of objects
// ({type, loc, msg, ...}) which crashes React if passed to toast.error().
// Convert it to a readable string once, here, for every call site.
api.interceptors.response.use(
  (res) => {
    // RBAC : action de collaborateur mise en file de validation → 202
    if (res.status === 202 && res.data?.pending_approval) {
      window.dispatchEvent(new Event("fc:approvals-changed"));
      const msg = "Action soumise à validation de l'administrateur (onglet Validations).";
      toast(msg, {
        icon: "⏳",
        style: { background: "#FFFBEB", color: "#92400E", border: "1px solid #FCD34D" },
      });
      window.__fcSuppressErrorToastUntil = Date.now() + 1500;
      const err = new Error("pending-approval");
      err.isPendingApproval = true;
      err.response = {
        status: 202,
        data: { detail: msg },
      };
      return Promise.reject(err);
    }
    return res;
  },
  (error) => {
    const d = error?.response?.data?.detail;
    if (d && typeof d !== "string") {
      error.response.data.detail = Array.isArray(d)
        ? d.map((e) => (e && typeof e === "object" ? e.msg || JSON.stringify(e) : String(e))).join(" · ")
        : d.msg || JSON.stringify(d);
    }
    return Promise.reject(error);
  }
);

export function fmtMoney(n, currency = "EUR") {
  const v = Number(n || 0);
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency, minimumFractionDigits: 2 }).format(v);
}

export function fmtDateFR(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
    return d.toLocaleDateString("fr-FR");
  } catch { return iso; }
}

// Convert an ISO datetime (typically UTC, e.g. "2026-06-08T09:30:00Z") to the
// time-of-day in Europe/Paris ("11:30"). Handles DST automatically.
export function fmtTimeParis(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Paris",
    }).format(d);
  } catch { return (iso || "").slice(11, 16); }
}

// Format an invoice's patient label: "Prénom Nom" or "Prénom Nom / Co-client" for
// couple sessions (Calendly multi-invitee). `snap` is the invoice.patient_snapshot.
export function fmtPatientLabel(snap) {
  if (!snap) return "";
  const primary = `${snap.first_name || ""} ${snap.last_name || ""}`.trim();
  const co = (snap.co_clients || []).map((c) => (c.name || "").trim()).filter(Boolean);
  return [primary, ...co].filter(Boolean).join(" / ");
}

// Format a date OR full ISO timestamp as "DD/MM/YYYY HH:mm" (Europe/Paris).
// If only YYYY-MM-DD is provided (no time component), returns just the date.
export function fmtDateTimeFR(iso) {
  if (!iso) return "";
  const s = String(iso);
  const hasTime = s.includes("T") || s.length > 10;
  if (!hasTime) return fmtDateFR(s);
  return `${fmtDateFR(s)} ${fmtTimeParis(s)}`;
}

export const INVOICE_STATUS_LABEL = {
  draft: "Brouillon",
  validated: "Validée",
  sent: "Envoyée",
  paid: "Payée",
  cancelled: "Annulée",
};

export const INVOICE_STATUS_TONE = {
  draft: { bg: "#EFF6FF", text: "#1E40AF", border: "#93C5FD" },
  validated: { bg: "#FFFBEB", text: "#92400E", border: "#FCD34D" },
  sent: { bg: "#F5F3FF", text: "#5B21B6", border: "#C4B5FD" },
  paid: { bg: "#ECFDF5", text: "#065F46", border: "#6EE7B7" },
  cancelled: { bg: "#F5F5F4", text: "#57534E", border: "#D6D3D1" },
};
